import { useState, useEffect } from 'react';
import { Plus, Download, FileText, Pencil, ChevronLeft, ChevronRight, Calendar as CalIcon, Phone, Bell, Trash2, Check, Lock, Eye, EyeOff, Package, X } from 'lucide-react';
import nhost from '../nhost';
import { sweDate } from '../helpers';
import { awardPoints } from '../points';
import WineModal from './WineModal';
import WineEditModal from './WineEditModal';

const EVENTS_QUERY = `
  query {
    events(order_by: { event_date: asc }) {
      id event_date event_time message reminder_days created_at event_type title cost published
      profile { id display_name }
      event_hosts { id profile_id profile { id display_name phone } }
      rsvps { id status user_id profile { display_name } }
      sessions { id }
      wines(where: { session_id: { _is_null: true } }, order_by: { created_at: asc }) {
        id name type vintage grape region price profile profile_source image_url
      }
    }
  }
`;
const MEMBERS_QUERY = `query { profiles(where: { approved: { _eq: true } }) { id display_name } }`;

function triggerConfetti(x, y) {
  const colors = ['#8B2A3E', '#F5E8EB', '#C0392B', '#E8B4B8', '#F7DC6F', '#D4A8B0'];
  for (let i = 0; i < 40; i++) {
    const el = document.createElement('div');
    el.style.cssText = `position:fixed;left:${x}px;top:${y}px;width:${Math.random()*8+4}px;height:${Math.random()*8+4}px;background:${colors[Math.floor(Math.random()*colors.length)]};border-radius:${Math.random()>0.5?'50%':'2px'};pointer-events:none;z-index:9999;transform:rotate(${Math.random()*360}deg);`;
    document.body.appendChild(el);
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 120 + 60;
    const vx = Math.cos(angle) * velocity;
    const vy = Math.sin(angle) * velocity - 80;
    let posX = x, posY = y, velY = vy, opacity = 1;
    const animate = () => {
      posX += vx * 0.03; velY += 4; posY += velY * 0.03; opacity -= 0.018;
      el.style.left = posX + 'px'; el.style.top = posY + 'px'; el.style.opacity = opacity;
      el.style.transform = `rotate(${posX * 2}deg)`;
      if (opacity > 0) requestAnimationFrame(animate); else el.remove();
    };
    requestAnimationFrame(animate);
  }
}

const MONTH_NAMES = ['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
const DAY_NAMES = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 18,
  padding: 16,
  marginBottom: 12,
  position: 'relative',
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

export default function Calendar({ profile, onViewMember, getCached, setCache, invalidateCache }) {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [allWines, setAllWines] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showWineModal, setShowWineModal] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeEventId, setActiveEventId] = useState(null);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());

  const [eDate, setEDate] = useState('');
  const [eTime, setETime] = useState('18:00');
  const [eHosts, setEHosts] = useState([profile.id]);
  const [eMessage, setEMessage] = useState('');
  const [eReminder, setEReminder] = useState(3);
  const [eType, setEType] = useState('tasting');
  const [eTitle, setETitle] = useState('');
  const [eCost, setECost] = useState('');
  const [saving, setSaving] = useState(false);
  const [startingSession, setStartingSession] = useState(null);
  const [showCalInfo, setShowCalInfo] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showWines, setShowWines] = useState({});
  const [editingWine, setEditingWine] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(null);

  useEffect(() => {
    fetchEvents();
    nhost.graphql.request(MEMBERS_QUERY).then(({ data }) => { if (data) setMembers(data.profiles); });
    nhost.graphql.request(`query { wines { id name grape type country region vintage profile profile_source price packaging } }`).then(({ data }) => { if (data) setAllWines(data.wines); });
    const d = new Date(); d.setDate(d.getDate() + 14);
    setEDate(d.toISOString().split('T')[0]);
  }, []);

  async function fetchEvents(force = false) {
    const cacheKey = 'events';
    if (!force) {
      const cached = getCached?.(cacheKey);
      if (cached) { setEvents(cached); return; }
    }
    const { data } = await nhost.graphql.request(EVENTS_QUERY);
    if (data) {
      setEvents(data.events);
      setCache?.(cacheKey, data.events);
    }
  }

  async function setRsvp(eventId, status, e) {
    const event = events.find(ev => ev.id === eventId);
    const existing = event?.rsvps.find(r => r.user_id === profile.id);
    if (existing) {
      if (existing.status === status) {
        await nhost.graphql.request(`mutation { delete_rsvp_by_pk(id: "${existing.id}") { id } }`);
      } else {
        await nhost.graphql.request(`mutation { update_rsvp_by_pk(pk_columns: {id: "${existing.id}"}, _set: {status: "${status}"}) { id } }`);
      }
    } else {
      await nhost.graphql.request(`mutation { insert_rsvp_one(object: { event_id: "${eventId}", user_id: "${profile.id}", status: "${status}" }) { id } }`);
    }
    if (status === 'going' && e) {
      const rect = e.target.getBoundingClientRect();
      triggerConfetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
      const ev = events.find(ev => ev.id === eventId);
      if (ev?.event_type === 'wine_box') await awardPoints(profile.id, 'winebox_rsvp', eventId);
    }
    invalidateCache?.('events');
    fetchEvents(true);
  }

  async function startSession(event) {
    setStartingSession(event.id);
    const { data } = await nhost.graphql.request(`
      mutation CreateSession($host_id: uuid!, $event_id: uuid!) {
        insert_sessions_one(object: { host_id: $host_id, event_id: $event_id }) { id }
      }
    `, { host_id: profile.id, event_id: event.id });
    if (data?.insert_sessions_one) {
      const sessionId = data.insert_sessions_one.id;
      await nhost.graphql.request(`
        mutation MoveWines($event_id: uuid!, $session_id: uuid!) {
          update_wines(where: { event_id: { _eq: $event_id }, session_id: { _is_null: true } } _set: { session_id: $session_id, event_id: null }) { affected_rows }
        }
      `, { event_id: event.id, session_id: sessionId });
    }
    setStartingSession(null);
    await fetchEvents();
  }

  function openWineModal(event) {
    setActiveSessionId(null);
    setActiveEventId(event.id);
    setShowWineModal(true);
  }

  function openEdit(e) {
    setEditingEvent(e);
    setEDate(e.event_date);
    setETime(e.event_time || '18:00');
    const hosts = e.event_hosts?.length > 0 ? e.event_hosts.map(h => h.profile_id) : [e.profile?.id || profile.id];
    setEHosts(hosts);
    setEMessage(e.message || '');
    setEReminder(e.reminder_days ?? 3);
    setEType(e.event_type || 'tasting');
    setETitle(e.title || '');
    setECost(e.cost ? String(e.cost) : '');
    setShowModal(true);
  }

  async function deleteEvent(id) {
    if (!window.confirm('Detta raderar eventet permanent. Om en provning är kopplad raderas den med alla viner och poäng. Kan inte ångras.')) return;
    await nhost.graphql.request(`mutation { delete_events_by_pk(id: "${id}") { id } }`);
    setShowModal(false);
    setEditingEvent(null);
    invalidateCache?.('events');
    await fetchEvents(true);
  }

  async function saveEvent() {
    if (!eDate) { alert('Välj datum'); return; }
    setSaving(true);
    const primaryHost = profile.id;
    if (editingEvent) {
      await nhost.graphql.request(`
        mutation UpdateEvent($id: uuid!, $changes: events_set_input!) {
          update_events_by_pk(pk_columns: { id: $id }, _set: $changes) { id }
        }
      `, { id: editingEvent.id, changes: { host_id: primaryHost, event_date: eDate, event_time: eTime || null, message: eMessage || null, reminder_days: eReminder, event_type: eType, title: eTitle.trim() || null, cost: eCost ? parseFloat(eCost) : null } });
      await nhost.graphql.request(`mutation { delete_event_hosts(where: { event_id: { _eq: "${editingEvent.id}" } }) { affected_rows } }`);
      await nhost.graphql.request(`
        mutation InsertHosts($objects: [event_hosts_insert_input!]!) { insert_event_hosts(objects: $objects) { affected_rows } }
      `, { objects: eHosts.map(id => ({ event_id: editingEvent.id, profile_id: id })) });
    } else {
      const { data } = await nhost.graphql.request(`
        mutation CreateEvent($obj: events_insert_input!) { insert_events_one(object: $obj) { id } }
      `, { obj: { host_id: primaryHost, event_date: eDate, event_time: eTime || null, message: eMessage || null, reminder_days: eReminder, event_type: eType, title: eTitle.trim() || null, cost: eCost ? parseFloat(eCost) : null } });
      if (data) {
        const eventId = data.insert_events_one.id;
        await nhost.graphql.request(`
          mutation InsertHosts($objects: [event_hosts_insert_input!]!) { insert_event_hosts(objects: $objects) { affected_rows } }
        `, { objects: eHosts.map(id => ({ event_id: eventId, profile_id: id })) });
        try {
          await fetch('/api/notify-event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eventId }) });
        } catch (err) { console.error('Failed to notify event', err); }
        await nhost.graphql.request(`mutation { insert_rsvp_one(object: { event_id: "${eventId}", user_id: "${profile.id}", status: "going" }) { id } }`);
      }
    }
    setSaving(false);
    setShowModal(false);
    setEditingEvent(null);
    setEMessage('');
    setEHosts([profile.id]);
    setEType('tasting');
    setETitle('');
    setECost('');
    await fetchEvents();
  }

  async function publishWineBox(event) {
    const hasSession = event.sessions?.length > 0;
    let sessionId = event.sessions?.[0]?.id ?? null;
    if (!hasSession) {
      const { data } = await nhost.graphql.request(`
        mutation CreateSession($host_id: uuid!, $event_id: uuid!) {
          insert_sessions_one(object: { host_id: $host_id, event_id: $event_id }) { id }
        }
      `, { host_id: profile.id, event_id: event.id });
      sessionId = data?.insert_sessions_one?.id ?? null;
    }
    if (sessionId) {
      await nhost.graphql.request(`
        mutation MoveWines($event_id: uuid!, $session_id: uuid!) {
          update_wines(where: { event_id: { _eq: $event_id }, session_id: { _is_null: true } } _set: { session_id: $session_id, event_id: null }) { affected_rows }
        }
      `, { event_id: event.id, session_id: sessionId });
    }
    await nhost.graphql.request(`mutation { update_events_by_pk(pk_columns: { id: "${event.id}" }, _set: { published: true }) { id } }`);
    invalidateCache?.('events');
    invalidateCache?.('wines');
    fetchEvents(true);
  }

  function changeMonth(d) {
    let m = calMonth + d, y = calYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setCalMonth(m); setCalYear(y);
  }

  const today = new Date();
  const eventDateMap = {};
  events.forEach(e => { eventDateMap[e.event_date] = e.event_type; });
  const first = new Date(calYear, calMonth, 1);
  let dow = first.getDay(); if (dow === 0) dow = 7;
  const dim = new Date(calYear, calMonth + 1, 0).getDate();

  const now = new Date(); now.setHours(0,0,0,0);
  const upcoming = events.filter(e => new Date(e.event_date) >= now);
  const past = events.filter(e => new Date(e.event_date) < now);

  function hostNames(e) {
    return (e.event_hosts?.length > 0 ? e.event_hosts : [{ profile: e.profile, profile_id: e.profile?.id }]);
  }

  return (
    <div>
      <div className="screen">

        {/* Månadsgrid */}
        <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 16, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <button onClick={() => changeMonth(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4, display: 'flex', alignItems: 'center' }}>
              <ChevronLeft size={20} strokeWidth={1.8} />
            </button>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--ink)' }}>
              {MONTH_NAMES[calMonth]} {calYear}
            </div>
            <button onClick={() => changeMonth(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4, display: 'flex', alignItems: 'center' }}>
              <ChevronRight size={20} strokeWidth={1.8} />
            </button>
          </div>
          <div className="cal-grid-days">
            {DAY_NAMES.map(d => <div key={d} className="cal-day-label">{d}</div>)}
            {Array.from({ length: dow - 1 }).map((_, i) => <div key={`e${i}`} className="cal-day" />)}
            {Array.from({ length: dim }).map((_, i) => {
              const d = i + 1;
              const ds = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const isToday = d === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
              const evType = eventDateMap[ds];
              const evBg = evType === 'wine_box' ? 'var(--gold-light)' : evType ? 'var(--wine-light)' : undefined;
              return (
                <div
                  key={d}
                  className={`cal-day this-month${isToday ? ' today' : ''}`}
                  style={evBg ? { background: evBg, position: 'relative' } : {}}
                >
                  {d}
                  {evType && <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: evType === 'wine_box' ? 'var(--gold)' : 'var(--wine)' }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Kalender-prenumeration */}
        {profile.calendar_token && (
          <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 16, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <CalIcon size={14} color="var(--wine)" strokeWidth={1.7} />
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)' }}>Kalender-prenumeration</span>
              </div>
              <button
                onClick={() => setShowCalInfo(v => !v)}
                style={{ background: 'none', border: '1px solid rgba(60,20,30,.12)', borderRadius: '50%', width: 22, height: 22, fontSize: 11, cursor: 'pointer', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 600 }}
              >i</button>
            </div>
            {showCalInfo && (
              <div style={{ background: 'var(--cream)', border: '1px solid rgba(60,20,30,.06)', borderRadius: 10, padding: '12px 14px', marginBottom: 10, fontSize: 12, color: 'var(--ink)', lineHeight: 1.7 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Så här prenumererar du:</div>
                <div style={{ marginBottom: 4 }}>· <strong>iPhone/iPad:</strong> Inställningar → Kalender → Konton → Lägg till konto → Annat → Prenumererad kalender</div>
                <div style={{ marginBottom: 4 }}>· <strong>Google Kalender:</strong> Inställningar → Lägg till kalender → Från URL</div>
                <div style={{ marginBottom: 4 }}>· <strong>Outlook:</strong> Lägg till kalender → Prenumerera från webben</div>
                <div>· <strong>Proton Calendar:</strong> Inställningar → Andra kalendrar → Lägg till från länk</div>
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
              Lägg till Vinligans events i din kalender-app. Kopiera länken och prenumerera.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, fontSize: 10, background: 'var(--cream)', border: '1px solid rgba(60,20,30,.07)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-all', color: 'var(--muted)', fontFamily: 'monospace' }}>
                {`https://vinligan.se/api/calendar?token=${profile.calendar_token}`}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(`https://vinligan.se/api/calendar?token=${profile.calendar_token}`); alert('Länken kopierades!'); }}
                style={{ flexShrink: 0, background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
              >
                Kopiera
              </button>
            </div>
          </div>
        )}

        {/* Protokoll */}
        <a
          href="/Vinprotokoll.pdf"
          download="Vinprovningsprotokoll.pdf"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'white', border: '1px solid rgba(60,20,30,.08)',
            borderRadius: 14, padding: '13px 16px',
            marginBottom: 12, color: 'var(--ink)', textDecoration: 'none',
          }}
        >
          <FileText size={18} color="var(--wine)" strokeWidth={1.7} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Ladda ner provningsprotokoll</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>PDF – fyll i under provningen</div>
          </div>
          <Download size={16} color="var(--muted)" strokeWidth={1.7} />
        </a>

        {/* Kommande */}
        {upcoming.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, marginTop: 4 }}>Kommande</div>
            {upcoming.map(e => {
              const myRsvp = e.rsvps.find(r => r.user_id === profile.id);
              const going = e.rsvps.filter(r => r.status === 'going');
              const notgoing = e.rsvps.filter(r => r.status === 'notgoing');
              const isHost = e.event_hosts?.some(h => h.profile_id === profile.id) || e.profile?.id === profile.id || profile.is_admin;
              const hasSession = e.sessions?.length > 0;
              const isBox = e.event_type === 'wine_box';
              return (
                <div key={e.id} style={cardStyle}>
                  {isHost && (
                    <button
                      onClick={() => openEdit(e)}
                      style={{ position: 'absolute', top: 14, right: 14, background: 'var(--wine-light)', border: 'none', borderRadius: 20, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--wine)', fontSize: 12, fontWeight: 500 }}
                    >
                      <Pencil size={12} strokeWidth={1.8} />
                      Redigera
                    </button>
                  )}

                  {/* Badge */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: isBox ? 'var(--gold-light)' : 'var(--wine-light)',
                    color: isBox ? '#7a5c00' : 'var(--wine)',
                    borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    marginBottom: 8,
                  }}>
                    {isBox ? <Package size={11} strokeWidth={1.8} /> : <CalIcon size={11} strokeWidth={1.8} />}
                    {isBox ? 'Vinlåda' : 'Kommande provning'}
                  </div>

                  <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)', paddingRight: isHost ? 90 : 0 }}>
                    {isBox ? (e.title || 'Vinlåda') : (
                      <>Provning av {hostNames(e).map((h, i, arr) => (
                        <span key={h.profile_id}>
                          <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }} onClick={() => onViewMember?.(h.profile?.id)}>{h.profile?.display_name}</span>
                          {i < arr.length - 2 && ', '}{i === arr.length - 2 && ' & '}
                        </span>
                      ))}</>
                    )}
                  </div>

                  <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                    {sweDate(e.event_date)}{e.event_time ? ` kl ${e.event_time}` : ''}
                  </div>

                  {e.cost && (
                    <div style={{ fontSize: 13, color: 'var(--ink)', marginTop: 4 }}>
                      Ca {e.cost} kr/person
                    </div>
                  )}

                  {e.message && (
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>"{e.message}"</div>
                  )}

                  {e.published && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--teal)', marginTop: 6 }}>
                      <Check size={12} strokeWidth={2} />
                      Publicerad – viner syns i vinlistan
                    </div>
                  )}

                  {e.event_hosts?.filter(h => h.profile?.phone).map(h => (
                    <a key={h.profile_id} href={`tel:${h.profile.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--wine)', marginTop: 6, textDecoration: 'none' }}>
                      <Phone size={13} strokeWidth={1.7} />
                      {h.profile.display_name}: {h.profile.phone}
                    </a>
                  ))}

                  {e.reminder_days > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                      <Bell size={11} strokeWidth={1.7} />
                      Påminnelse {e.reminder_days} dag{e.reminder_days > 1 ? 'ar' : ''} innan via e-post
                    </div>
                  )}

                  {/* RSVP */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button
                      onClick={(ev) => setRsvp(e.id, 'going', ev)}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none',
                        background: myRsvp?.status === 'going' ? 'var(--wine)' : 'var(--wine-light)',
                        color: myRsvp?.status === 'going' ? '#fff' : 'var(--wine)',
                      }}
                    >
                      {isBox ? 'Ja' : 'Jag kommer'}
                    </button>
                    <button
                      onClick={() => setRsvp(e.id, 'notgoing')}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        background: myRsvp?.status === 'notgoing' ? 'rgba(163,45,45,.1)' : '#fff',
                        color: myRsvp?.status === 'notgoing' ? '#a32d2d' : 'var(--muted)',
                        border: '1px solid rgba(60,20,30,.12)',
                      }}
                    >
                      {isBox ? 'Nej' : 'Kan ej'}
                    </button>
                  </div>

                  {going.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--teal)', marginTop: 8, fontWeight: 500 }}>
                      {isBox ? 'Accepterat' : 'Kommer'} ({going.length}):{' '}
                      {going.map((r, i) => (
                        <span key={r.id}>
                          <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }} onClick={() => onViewMember?.(r.user_id)}>{r.profile?.display_name}</span>
                          {i < going.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                  )}
                  {notgoing.length > 0 && (
                    <div style={{ fontSize: 12, color: '#a32d2d', marginTop: 4 }}>
                      {isBox ? 'Tackat nej' : 'Kan ej'} ({notgoing.length}): {notgoing.map(r => r.profile?.display_name).join(', ')}
                    </div>
                  )}

                  {!isHost && e.wines?.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                      {e.wines.length} vin{e.wines.length !== 1 ? 'er' : ''} redan planerade
                    </div>
                  )}

                  {isHost && e.wines?.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 10, ...divider }}>
                      <button
                        onClick={() => setShowWines(prev => ({ ...prev, [e.id]: !prev[e.id] }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5 }}
                      >
                        {showWines[e.id]
                          ? <><EyeOff size={13} strokeWidth={1.7} /> Dölj planerade viner</>
                          : <><Lock size={13} strokeWidth={1.7} /> Visa planerade viner ({e.wines.length})</>
                        }
                      </button>
                      {showWines[e.id] && e.wines.map(w => (
                        <div key={w.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: 'var(--ink)', padding: '4px 0' }}>
                          <span>{w.name}{w.vintage ? ` (${w.vintage})` : ''} · <span style={{ color: 'var(--muted)' }}>{w.type}</span></span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setEditingWine(w)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '0 4px', lineHeight: 1 }}>
                              <Pencil size={13} strokeWidth={1.7} />
                            </button>
                            <button
                              onClick={async () => { if (!window.confirm(`Ta bort ${w.name}?`)) return; await nhost.graphql.request(`mutation { delete_wines_by_pk(id: "${w.id}") { id } }`); fetchEvents(true); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '0 4px', display: 'flex' }}
                            ><X size={14} strokeWidth={1.8} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isHost && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                      {isBox ? (
                        <>
                          <button
                            onClick={() => openWineModal(e)}
                            style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                          >
                            + Lägg till viner i lådan
                          </button>
                          {!e.published && (
                            <button
                              onClick={() => publishWineBox(e)}
                              style={{ background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                            >
                              Publicera vinlådan
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {!hasSession ? (
                            <button
                              onClick={() => startSession(e)}
                              disabled={startingSession === e.id}
                              style={{ background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: startingSession === e.id ? 0.7 : 1 }}
                            >
                              {startingSession === e.id ? 'Startar...' : 'Starta provning'}
                            </button>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--teal)' }}>
                              <Check size={13} strokeWidth={2} />
                              Provning startad – syns under Provningar-fliken
                            </div>
                          )}
                          <button
                            onClick={() => openWineModal(e)}
                            style={{ background: '#fff', color: 'var(--wine)', border: '1px solid rgba(139,42,62,.2)', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                          >
                            + Lägg till viner
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {profile.is_admin && (
                    <div style={{ borderTop: '1px dashed rgba(139,42,62,.12)', marginTop: 14, paddingTop: 10 }}>
                      <button
                        onClick={() => setAdminMenuOpen(adminMenuOpen === e.id ? null : e.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11.5, fontWeight: 500, padding: 0, fontFamily: 'inherit' }}
                      >
                        <span style={{ fontSize: 12 }}>⚙</span>
                        Admin
                        <span style={{ fontSize: 9 }}>{adminMenuOpen === e.id ? '▲' : '▼'}</span>
                      </button>
                      {adminMenuOpen === e.id && (
                        <div style={{ marginTop: 10 }}>
                          <button
                            onClick={() => deleteEvent(e.id)}
                            style={{ background: 'rgba(185,28,28,.06)', border: '1px solid rgba(185,28,28,.2)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, color: '#B91C1C', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left' }}
                          >
                            Radera event
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Tidigare */}
        {past.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, marginTop: 8 }}>Tidigare evenemang</div>
            {past.map(e => {
              const isHost = e.event_hosts?.some(h => h.profile_id === profile.id) || e.profile?.id === profile.id || profile.is_admin;
              const hasSession = e.sessions?.length > 0;
              return (
              <div key={e.id} style={{ ...cardStyle, opacity: 0.72 }}>
                {profile.is_admin && (
                  <button
                    onClick={() => openEdit(e)}
                    style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4 }}
                  >
                    <Pencil size={14} strokeWidth={1.7} />
                  </button>
                )}
                <div style={{ fontSize: 15, fontWeight: 500, fontFamily: 'var(--font-display)' }}>
                  {e.event_type === 'wine_box' ? (e.title || 'Vinlåda') : (
                    <>Provning av {hostNames(e).map((h, i, arr) => (
                      <span key={h.profile?.id || i}>
                        <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }} onClick={() => onViewMember?.(h.profile?.id)}>{h.profile?.display_name}</span>
                        {i < arr.length - 2 && ', '}{i === arr.length - 2 && ' & '}
                      </span>
                    ))}</>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{sweDate(e.event_date)}</div>
                {e.sessions?.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--teal)', marginTop: 4 }}>
                    <Check size={12} strokeWidth={2} />
                    {e.event_type === 'wine_box' ? 'Publicerad' : 'Provning genomförd'}
                  </div>
                )}

                {isHost && e.event_type !== 'wine_box' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingTop: 10, ...divider }}>
                    {!hasSession && (
                      <button
                        onClick={() => startSession(e)}
                        disabled={startingSession === e.id}
                        style={{ background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: startingSession === e.id ? 0.7 : 1 }}
                      >
                        {startingSession === e.id ? 'Startar...' : 'Starta provning'}
                      </button>
                    )}
                    <button
                      onClick={() => openWineModal(e)}
                      style={{ background: '#fff', color: 'var(--wine)', border: '1px solid rgba(139,42,62,.2)', borderRadius: 12, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                    >
                      + Lägg till viner
                    </button>
                  </div>
                )}

                {profile.is_admin && (
                  <div style={{ borderTop: '1px dashed rgba(139,42,62,.12)', marginTop: 14, paddingTop: 10 }}>
                    <button
                      onClick={() => setAdminMenuOpen(adminMenuOpen === e.id ? null : e.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11.5, fontWeight: 500, padding: 0, fontFamily: 'inherit' }}
                    >
                      <span style={{ fontSize: 12 }}>⚙</span>
                      Admin
                      <span style={{ fontSize: 9 }}>{adminMenuOpen === e.id ? '▲' : '▼'}</span>
                    </button>
                    {adminMenuOpen === e.id && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => deleteEvent(e.id)}
                          style={{ background: 'rgba(185,28,28,.06)', border: '1px solid rgba(185,28,28,.2)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, color: '#B91C1C', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left' }}
                        >
                          Radera event
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </>
        )}

        {!upcoming.length && !past.length && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
            <CalIcon size={32} color="rgba(60,20,30,.18)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 14 }}>Inga provningar planerade. Tryck + för att planera nästa!</div>
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        className="fab"
        onClick={() => {
          setEditingEvent(null);
          setEDate(new Date(Date.now() + 14*24*60*60*1000).toISOString().split('T')[0]);
          setETime('18:00'); setEHosts([profile.id]); setEMessage('');
          setEReminder(3); setEType('tasting'); setETitle(''); setECost('');
          setShowModal(true);
        }}
      >
        <Plus size={20} strokeWidth={2} />
        Planera
      </button>

      {/* Modal: Planera/redigera event */}
      {showModal && (
        <div className="modal-bg" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editingEvent ? 'Redigera' : 'Planera'}</div>

            {!editingEvent && (
              <div className="field">
                <label>Typ av event</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['tasting', 'Provning'], ['wine_box', 'Vinlåda']].map(([val, label]) => (
                    <div key={val} onClick={() => setEType(val)} style={{ flex: 1, textAlign: 'center', padding: '10px 8px', borderRadius: 12, border: `1px solid ${eType === val ? 'var(--wine)' : 'rgba(60,20,30,.12)'}`, background: eType === val ? 'var(--wine-light)' : 'white', color: eType === val ? 'var(--wine)' : 'var(--ink)', fontWeight: eType === val ? 600 : 400, fontSize: 14, cursor: 'pointer' }}>
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {eType === 'wine_box' && (
              <>
                <div className="field"><label>Namn på vinlådan</label><input value={eTitle} onChange={e => setETitle(e.target.value)} placeholder="t.ex. Vinlådan Oktober" /></div>
                <div className="field"><label>Ungefärlig kostnad (kr/person)</label><input type="number" value={eCost} onChange={e => setECost(e.target.value)} placeholder="t.ex. 200" /></div>
              </>
            )}
            {eType === 'tasting' && (
              <div className="field">
                <label>Kostnad (kr/person)</label>
                <input type="number" value={eCost} onChange={e => setECost(e.target.value)} placeholder="t.ex. 150 (visas ej förrän viner avslöjas)" />
              </div>
            )}

            <div className="field"><label>Datum</label><input type="date" value={eDate} onChange={e => setEDate(e.target.value)} /></div>
            {eType !== 'wine_box' && <div className="field"><label>Tid</label><input type="time" value={eTime} onChange={e => setETime(e.target.value)} /></div>}
            <div className="field">
              <label>Värd{eHosts.length > 1 ? 'ar' : ''}</label>
              <div className="host-grid">
                {members.map(m => (
                  <div key={m.id} className={`host-btn ${eHosts.includes(m.id) ? 'selected' : ''}`} onClick={() => { setEHosts(prev => prev.includes(m.id) ? prev.length > 1 ? prev.filter(id => id !== m.id) : prev : [...prev, m.id]); }} style={{ opacity: 1 }}>
                    {m.display_name}
                    {eHosts.includes(m.id) && <span style={{ fontSize: 10, display: 'block', color: 'var(--teal)' }}>✓ Värd</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="field">
              <label>E-postpåminnelse</label>
              <select value={eReminder} onChange={e => setEReminder(parseInt(e.target.value))}>
                <option value={0}>Ingen påminnelse</option>
                <option value={1}>1 dag innan</option>
                <option value={3}>3 dagar innan</option>
                <option value={7}>1 vecka innan</option>
              </select>
            </div>
            <div className="field"><label>Meddelande till gruppen</label><textarea value={eMessage} onChange={e => setEMessage(e.target.value)} placeholder="t.ex. Tema: Burgunder. Ta med något ni vill dela!" /></div>

            {editingEvent && (
              <button
                onClick={() => deleteEvent(editingEvent.id)}
                style={{ width: '100%', background: 'none', border: '1px solid rgba(163,45,45,.3)', color: '#a32d2d', borderRadius: 12, padding: '10px', fontSize: 13, cursor: 'pointer', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Trash2 size={14} strokeWidth={1.7} />
                Ta bort provning
              </button>
            )}
            <div className="btn-row">
              <button className="btn-secondary" onClick={() => { setShowModal(false); setEditingEvent(null); }}>Avbryt</button>
              <button className="btn-primary" onClick={() => saveEvent()} disabled={saving}>{saving ? 'Sparar...' : editingEvent ? 'Spara ändringar' : 'Skicka inbjudan'}</button>
            </div>
          </div>
        </div>
      )}

      {editingWine && (
        <WineEditModal wine={editingWine} onClose={() => setEditingWine(null)} onSaved={() => { setEditingWine(null); fetchEvents(true); }} />
      )}

      {showWineModal && (
        <WineModal
          profile={profile} wines={allWines} sessionId={activeSessionId} eventId={activeEventId}
          onClose={() => { setShowWineModal(false); setActiveSessionId(null); setActiveEventId(null); }}
          onSaved={() => { setShowWineModal(false); setActiveSessionId(null); setActiveEventId(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}
